import type { WireItem } from "../api/types.js";
import { combinedTireField, formatDateTime, formatEmployee } from "../domain/format.js";

/**
 * Same columns, same order, same headers as `MainWindow.xaml:38-56`.
 * Diacritics restored (`Št.`, `Reg. št.`, `Platišča`, `Označil za
 * dvignjeno`, …) where the legacy XAML used an ASCII-flattened approximation
 * (`Platisca`, `Oznacil za dvignjeno`) — the task brief itself does the same
 * for the picked-up filter labels ("V skladišču" vs. the XAML's
 * "V skladiscu"), so this follows that convention rather than the raw file
 * bytes.
 *
 * Header "Izdal potrdilo" ("issued the receipt") binds to `receivedBy` and
 * "Označil za dvignjeno" ("marked as picked up") binds to `issuedBy` — that
 * is genuinely how the legacy XAML wires them (MainWindow.xaml:55-56), not a
 * transcription slip: `receivedBy` is who checked the tires in and handed
 * over the intake receipt, `issuedBy` is who processed the pickup.
 */
export interface ItemColumn {
  key: string;
  header: string;
  width: string;
  /** Tire-set columns stack one value per stored set (`\n`-joined) and need `white-space: pre-line`. */
  multiline?: boolean;
  render: (item: WireItem) => string;
}

export const ITEM_COLUMNS: ItemColumn[] = [
  { key: "number", header: "Št.", width: "56px", render: (i) => (i.number !== null ? String(i.number) : "") },
  { key: "lastname", header: "Priimek", width: "150px", render: (i) => i.lastname ?? "" },
  { key: "name", header: "Ime", width: "130px", render: (i) => i.name ?? "" },
  { key: "registrationNumber", header: "Reg. št.", width: "130px", render: (i) => i.registrationNumber ?? "" },
  { key: "carMaker", header: "Znamka avta", width: "130px", render: (i) => i.carMaker ?? "" },
  { key: "tyreMaker", header: "Znamka gum", width: "140px", multiline: true, render: (i) => combinedTireField(i.tireSets, "maker") },
  { key: "dimension", header: "Dimenzija", width: "120px", multiline: true, render: (i) => combinedTireField(i.tireSets, "dimension") },
  { key: "usage", header: "Obraba", width: "80px", multiline: true, render: (i) => combinedTireField(i.tireSets, "usage") },
  { key: "quantity", header: "Kom.", width: "56px", multiline: true, render: (i) => combinedTireField(i.tireSets, "quantity") },
  { key: "rimsType", header: "Platišča", width: "70px", multiline: true, render: (i) => combinedTireField(i.tireSets, "rimsType") },
  { key: "receivedAt", header: "Datum vnosa", width: "120px", render: (i) => formatDateTime(i.receivedAt) },
  { key: "pickedUpAt", header: "Datum dviga", width: "120px", render: (i) => formatDateTime(i.pickedUpAt) },
  { key: "phone", header: "Telefon", width: "100px", render: (i) => i.phone ?? "" },
  { key: "notes", header: "Opombe", width: "150px", render: (i) => i.notes ?? "" },
  { key: "receivedBy", header: "Izdal potrdilo", width: "140px", render: (i) => formatEmployee(i.receivedBy) },
  { key: "issuedBy", header: "Označil za dvignjeno", width: "140px", render: (i) => formatEmployee(i.issuedBy) },
];
